import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { useResizeObserver, useCopyToClipboard } from 'usehooks-ts';
import { isMobile } from 'react-device-detect';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Input, Modal, Table, Select, DatePicker, Tooltip, Form, Switch, Flex, notification, InputNumber, Drawer, Button, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    CarOutlined,
    DeleteOutlined,
    PlusOutlined,
    PrinterOutlined,
    QuestionCircleOutlined,
    EyeFilled,
    CheckCircleOutlined,
    CopyOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';

import { usePDF } from '@react-pdf/renderer';
import printJS from 'print-js';

import ConsignmentCard, { IBoxGroupCardItem } from '@components/consignment-card/consignment-card';
import CargoDetails from '@flp/components/logistics/cargo-details/cargo-details';

import { TransitWarehouseTag } from '@controls/tags';
import Comment from '@controls/comment/comment';
import Processing from '@controls/processing';
import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import TruckAcceptanceWithBoxesPdf from '@core/print-forms/truck-acceptance-with-boxes/truck-acceptance-with-boxes-pdf';

import { info, exception, securityRestriction } from '@extensions/notification';
import { getEnumList, delayAction, getDate, dataURItoBlob } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@flp/server';

import { IUserSession } from '@entities/user-session';
import { ITruckFilter } from '@entities/truck-filter';
import { ITruck } from '@entities/truck';
import { IConsignment } from '@entities/consignment';
import { ICountry } from '@entities/country';
import { ICity } from '@entities/city';

import { TruckStatus, enumLabel as truckStatusLabel } from '@enums/truck-status';
import { Permission, hasPermission } from '@enums/permission';
import { IEnumItem } from '@enums/enum-item';
import { BillStatus } from '@enums/bill-status';

import { TariffIcon, BoxesIcon } from '@icons/index';

dayjs.extend(utc);

const filterContext: string = 'ImportTrucks';

const Trucks = () => {
    const initFilter: ITruckFilter = {
        statuses: [TruckStatus.New, TruckStatus.OnWay],
        isArchived: false,
    };

    const { RangePicker } = DatePicker;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [modal, contextHolder] = Modal.useModal();
    const [api, notificationContextHolder] = notification.useNotification();

    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const filter = useAppSelector<ITruckFilter>((s) => s.filters[filterContext]);

    const [trucks, setTrucks] = useState<Array<ITruck>>([]);
    const [currentTruck, setCurrentTruck] = useState<ITruck>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [showFilter, setShowFilter] = useState<boolean>(!isMobile);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(TruckStatus, truckStatusLabel));
    const [cities, setCities] = useState<Array<ICity>>([]);

    const [loadingPrintData, setLoadingPrintData] = useState(false);

    const [startAcceptancePrint, setStartAcceptancePrint] = useState<boolean>(false);
    const [printAcceptanceData, setPrintAcceptanceData] = useState<any>();
    const [pdfAcceptanceInstance, updateAcceptancePdf] = usePDF();

    const [startPrint, setStartPrint] = useState<boolean>(false);
    const [openDetails, setOpenDetails] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;

        setCountries([]);
        setTrucks([]);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('countries', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('trucks/import', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения машин', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountries(result[0][0] || []);
                setCities(result[0][1] || []);
                setTrucks(result[0][2] || []);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (printAcceptanceData) {
            updateAcceptancePdf(<TruckAcceptanceWithBoxesPdf printData={printAcceptanceData} countries={countries} />);
            setLoadingPrintData(false);
            setStartAcceptancePrint(true);
        }
    }, [printAcceptanceData]);

    useEffect(() => {
        if (startAcceptancePrint && !pdfAcceptanceInstance.loading && pdfAcceptanceInstance.blob) {
            setStartAcceptancePrint(false);
            setPrintAcceptanceData(undefined);

            const blobURL = URL.createObjectURL(pdfAcceptanceInstance.blob);
            printJS(blobURL);
        }
    }, [startAcceptancePrint, pdfAcceptanceInstance]);

    const [, copy] = useCopyToClipboard();

    const handleCopy = (text: string) => () => {
        copy(text).then(() => {
            info(api, `"${text}" скопирован`);
        });
    };

    const onDelete = () => {
        if (!currentTruck) return;

        serverFetch(`trucks/${currentTruck.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления машины', ex, () => d(userLoaded(undefined)));
            });
    };

    const getPrintAcceptanceDataWithBoxes = () => {
        setLoadingPrintData(true);

        serverFetch(`trucks/${currentTruck?.id}/print`, { method: 'GET' })
            .then((data) => {
                const printData = {
                    truckNumber: currentTruck?.number,
                    consignments: data,
                };

                setPrintAcceptanceData(printData);
            })
            .catch((ex) => {
                setLoadingPrintData(false);
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить машину',
                        key: 'add',
                        type: 'primary',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageTruck)) {
                                securityRestriction(api, [Permission.ManageTruck]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: `${
                            hasPermission(userSession.permissions, Permission.ManageTruck) ? 'Управление машиной' : 'Информация о машине'
                        }`,
                        key: 'edit',
                        disabled: !currentTruck,
                        icon: <CarOutlined />,
                        onClick: () => {
                            navigate(`${resolved.pathname}/${currentTruck?.id}`);
                        },
                    },
                    {
                        label: 'Удалить машину',
                        key: 'delete',
                        disabled:
                            !currentTruck ||
                            (currentTruck.status !== TruckStatus.New && !hasPermission(userSession.permissions, Permission.FullAccess)),
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteTruck)) {
                                securityRestriction(api, [Permission.DeleteTruck]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить машину "${currentTruck?.number}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Детали поставки',
                        key: 'consignments',
                        disabled: !currentTruck,
                        icon: <UnorderedListOutlined />,
                        onClick: () => {
                            setOpenDetails(true);
                        },
                        hidden: isMobile,
                    },
                    {
                        label: 'Управление грузом',
                        key: 'manageConsignments',
                        disabled: !currentTruck,
                        icon: <BoxesIcon />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageTruckConsignment) &&
                                !hasPermission(userSession.permissions, Permission.ViewTruckConsignment)
                            ) {
                                securityRestriction(api, [Permission.ManageTruckConsignment, Permission.ViewTruckConsignment]);
                                return;
                            }

                            navigate(`${resolved.pathname}/consignments/${currentTruck?.id}`);
                        },
                        hidden: isMobile,
                    },
                    {
                        label: 'Управление расчетом',
                        key: 'tariffs',
                        disabled: !currentTruck,
                        icon: <TariffIcon />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageTransportBill) &&
                                !hasPermission(userSession.permissions, Permission.EditTransportBill) &&
                                !hasPermission(userSession.permissions, Permission.ViewTransportBill)
                            ) {
                                securityRestriction(api, [
                                    Permission.ManageTransportBill,
                                    Permission.EditTransportBill,
                                    Permission.ViewTransportBill,
                                ]);
                                return;
                            }

                            navigate(`${resolved.pathname}/tariffs/${currentTruck?.id}`);
                        },
                        hidden: isMobile,
                    },
                    { isDivider: true },
                    {
                        label: 'Печать',
                        key: 'print',
                        disabled: loading || !currentTruck || startAcceptancePrint || startPrint,
                        icon: <PrinterOutlined />,
                        hidden: isMobile,
                        children: [
                            {
                                key: 'withoutBoxes',
                                label: 'Без коробок',
                                icon: <PrinterOutlined />,
                                onClick: () => {
                                    setStartPrint(true);

                                    serverFetch(`trucks/print`, { method: 'POST', bodyData: [currentTruck?.id] })
                                        .then((data) => {
                                            const fileBody = `data:${data.contentType};base64,${data.fileContents}`;

                                            const blobURL = URL.createObjectURL(dataURItoBlob(fileBody));
                                            printJS(blobURL);

                                            setStartPrint(false);
                                        })
                                        .catch((ex) => {
                                            setStartPrint(false);
                                            exception(api, 'Ошибка выгрузки листа приемки', ex, () => d(userLoaded(undefined)));
                                        });
                                },
                            },
                            {
                                key: 'withBoxes',
                                label: 'С коробками',
                                icon: <PrinterOutlined />,
                                onClick: () => {
                                    getPrintAcceptanceDataWithBoxes();
                                },
                            },
                        ],
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 150 }}
                            key='number'
                            placeholder='Машина'
                            value={filter?.number}
                            onChange={(data) => {
                                d(setFilter({ ...filter, number: data.target.value }, filterContext));
                            }}
                        />,
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 180 }}
                            key='searchText'
                            placeholder='Клиент'
                            value={filter?.searchText}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 180 }}
                            key='legalName'
                            placeholder='Контрагент'
                            value={filter?.legalName}
                            onChange={(data) => {
                                d(setFilter({ ...filter, legalName: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 160 }}
                            key='tradingPlatform'
                            placeholder='Торговая площадка'
                            value={filter?.tradingPlatform}
                            onChange={(data) => {
                                d(setFilter({ ...filter, tradingPlatform: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((s) => {
                                return { value: s.id, label: s.name };
                            })}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус машины'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='departure'
                            format='DD.MM.YYYY'
                            placeholder={['Отгрузка с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.departureFrom ? dayjs(filter?.departureFrom) : null,
                                filter?.departureTo ? dayjs(filter?.departureTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                departureFrom: undefined,
                                                departureTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            departureFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            departureTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='finishedOn'
                            format='DD.MM.YYYY'
                            placeholder={['Приемка с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.finishedFrom ? dayjs(filter?.finishedFrom) : null,
                                filter?.finishedTo ? dayjs(filter?.finishedTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                finishedFrom: undefined,
                                                finishedTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            finishedFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            finishedTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isArchived}
                                onChange={(value: any) => {
                                    d(setFilter({ ...filter, statuses: [], isArchived: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const item = trucks.find((o) => o.id === selectedRowKeys[0]);
            setCurrentTruck(item);
        } else {
            setCurrentTruck(undefined);
        }

        const items: Array<IConsignment> = [];
        selectedRowKeys.map((e) => {
            const item = trucks.find((o) => o.id === e);
            if (item && item.consignments) items.push(...item.consignments);
        });
    };

    const renderTable = () => {
        const columns: ColumnsType<ITruck> = [
            {
                title: 'Машина',
                key: 'number',
                width: 200,
                render: (_, record) => {
                    if (!record.number) return;

                    return (
                        <Flex className='copy-container' align='center' justify='space-between'>
                            <Button
                                variant='link'
                                color='blue'
                                onClick={() => setOpenDetails(true)}
                                style={{ height: 'fit-content', padding: 0 }}
                            >
                                {record.number}
                            </Button>
                            <Tooltip title='Копировать' color='cyan'>
                                <Button
                                    style={{ height: 'fit-content', padding: 0 }}
                                    className='hide-copy'
                                    size='small'
                                    variant='link'
                                    color='cyan'
                                    icon={<CopyOutlined />}
                                    onClick={handleCopy(record.number)}
                                />
                            </Tooltip>
                        </Flex>
                    );
                },
            },
            {
                width: 40,
                align: 'center',
                render: (_, record) => {
                    return record.comment && <Comment comment={record.comment} />;
                },
            },
            {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (_, record) => {
                    return (
                        <>
                            {record.status ? truckStatusLabel(record.status) : 'Не назначена'}
                            {record.trackingUrl && (
                                <Tooltip placement='topRight' title='Показать на карте'>
                                    <a className='trackingUrl' href={record.trackingUrl} target='_blank' rel='noreferrer'>
                                        <EyeFilled />
                                    </a>
                                </Tooltip>
                            )}
                        </>
                    );
                },
            },
            {
                title: 'Отгрузка',
                width: 100,
                align: 'center',
                render: (_, record) => {
                    return getDate(record.departureOn);
                },
            },
            {
                title: 'Приемка',
                width: 100,
                align: 'center',
                render: (_, record) => {
                    return getDate(record.finishedOn);
                },
            },
            { title: 'Маршрут', dataIndex: 'waypointHeaderName', key: 'waypointHeaderName', width: 200 },
            {
                title: 'Точка маршрута',
                width: 250,
                render: (_, record) => {
                    if(!record.currentTrackpointName)
                        return;

                    return <Tag variant='outlined' color='geekblue'>{record.currentTrackpointName}</Tag>;
                },
            },
            { title: 'Перевозчик', dataIndex: 'cargoCarrier', key: 'cargoCarrier', width: 200 },
            {
                title: 'Кол-во / шт',
                dataIndex: 'totalQty',
                key: 'totalQty',
                width: 120,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 700,
                    },
                }),
            },
            {
                width: 40,
                align: 'center',
                render: (_, record) => {
                    if (record.billStatus && record.billStatus != BillStatus.New) {
                        return (
                            <Tooltip
                                placement='top'
                                title={
                                    record.billStatus == BillStatus.Completed ||
                                    record.billStatus == BillStatus.Paid ||
                                    record.billStatus == BillStatus.Abandoned
                                        ? 'Расчет Проведен'
                                        : 'Расчет проведен частично'
                                }
                                color={
                                    record.billStatus == BillStatus.Completed ||
                                    record.billStatus == BillStatus.Paid ||
                                    record.billStatus == BillStatus.Abandoned
                                        ? 'var(--main-green)'
                                        : 'darkorange'
                                }
                            >
                                <CheckCircleOutlined
                                    style={{
                                        color:
                                            record.billStatus == BillStatus.Completed ||
                                            record.billStatus == BillStatus.Paid ||
                                            record.billStatus == BillStatus.Abandoned
                                                ? 'var(--main-green)'
                                                : 'darkorange',
                                        fontSize: 18,
                                        marginTop: 5,
                                    }}
                                />
                            </Tooltip>
                        );
                    }

                    return <></>;
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                dataSource={trucks}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                }}
                onRow={(record) => {
                    return {
                        onClick: () => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58), x: 'max-content' }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {currentTruck && openDetails && (
                <Drawer
                    closable={false}
                    destroyOnHidden
                    title={`Детали поставки "${currentTruck.number}"`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenDetails(false)}
                    size={1000}
                    extra={
                        <Button variant='link' color='blue' style={{ fontWeight: 600, fontSize: 16 }} onClick={() => setOpenDetails(false)}>
                            Закрыть
                        </Button>
                    }
                >
                    <CargoDetails
                        path='trucks/import'
                        truckId={currentTruck.id || ''}
                        filter={filter}
                        countries={countries}
                        api={api}
                        serverFetch={serverFetch}
                    />
                </Drawer>
            )}

            {(loadingPrintData || startAcceptancePrint || startPrint) && <Processing title='Печать...' />}

            {contextHolder}
            {notificationContextHolder}
        </div>
    );
};

export default Trucks;
